import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useHabitDaysRequestStore } from '../stores/habitDaysRequestStore';
import { IntervalSelect } from './views/HabitsView/IntervalSelect';
import { missingDaysMessage } from '../utils/habitSchedule';
import type { HabitSchedule } from '../types';

/**
 * The form the no-form paths open to pick a habit's days (stores/habitDaysRequestStore).
 * ⚠️ MOUNTED ONCE in App: a request can come from a service with no UI of its own
 * (the AI coach, a quest proposal), so the dialog cannot live beside its caller.
 * Shows the FRONT of the queue; Confirm stays disabled until a day is picked.
 */
export function HabitDaysPrompt() {
  const request = useHabitDaysRequestStore(s => s.queue[0]);
  const answer = useHabitDaysRequestStore(s => s.answer);
  const [schedule, setSchedule] = useState<HabitSchedule | null>(null);

  // A fresh request starts from ITS frequency, with an empty days list — the pickers
  // only toggle a list that exists (IntervalSelect).
  useEffect(() => {
    if (!request) { setSchedule(null); return; }
    const s = request.schedule;
    setSchedule({
      ...s,
      ...(s.interval === 'weekly' ? { days: s.days ?? [] } : {}),
      ...(s.interval === 'monthly' ? { monthDays: s.monthDays ?? [] } : {}),
      ...(s.interval === 'yearly' ? { yearDates: s.yearDates ?? [] } : {}),
    });
  }, [request?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!request || !schedule) return null;
  const missing = missingDaysMessage(schedule);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="habit-days-title">
      <div className="w-full max-w-md rounded-xl border border-border bg-card shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-border p-5">
          <div>
            <h2 id="habit-days-title" className="text-base font-semibold text-foreground">Pick the days for “{request.name}”</h2>
            <p className="mt-1 text-xs text-muted-foreground">A {schedule.interval} habit needs at least one day, or it never comes due.</p>
          </div>
          <button type="button" aria-label="Cancel" onClick={() => answer(request.id, null)} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5">
          <IntervalSelect value={schedule} onChange={setSchedule} />
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-border p-5">
          <button type="button" onClick={() => answer(request.id, null)} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
            Cancel
          </button>
          <button
            type="button"
            disabled={!!missing}
            onClick={() => answer(request.id, schedule)}
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Confirm days
          </button>
        </div>
      </div>
    </div>
  );
}
